import React, { useEffect, useState } from "react";
import UserService from "../../services/UserService/UserService.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { normalizeUserProperties } from "../../utils/NormalizationUtil";
import {
  NormalizedUser,
  OfficesList,
  UserAccessType,
  UsersList,
} from "../../types/user.ts";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const UserManagement = () => {
  const [users, setUsers] = useState<UsersList>({});
  const [offices, setOffices] = useState<OfficesList>({});
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>("");
  const [userData, setUserData] = useState<NormalizedUser | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();

  const displayAlert = (message: string, type: FadeAlertType) => {
    setAlert(message);
    setAlertType(type);
  };

  useEffect(() => {
    const fetchUsersAndOffices = async () => {
      try {
        const [userResult, officeResult] = await Promise.all([
          UserService.getMyUsers(jwt),
          InsuranceService.getAllOffices(jwt),
        ]);
        if (typeof userResult === "object" && !Array.isArray(userResult)) {
          setUsers(userResult as UsersList);
        } else throw new Error("Грешка при зареждането на потребители");
        if (typeof officeResult === "object" && !Array.isArray(officeResult)) {
          setOffices(officeResult as OfficesList);
        } else throw new Error("Грешка при зареждането на офиси");
      } catch (error) {
        displayAlert(error.message, "error");
      }
    };
    fetchUsersAndOffices();
  }, [jwt]);

  const fetchUserData = async (userId: string) => {
    if (!userId) return;
    try {
      const data = await UserService.getUserData(jwt, userId);
      const normalizedData = normalizeUserProperties(
        data.userData
      ) as NormalizedUser;
      setUserData(normalizedData);
    } catch (error) {
      displayAlert("Грешка при зареждането на потребител", "error");
    }
  };

  const saveUserData = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (userData) {
        if (!userData.uid) userData.uid = 0;
        const normalizedData = normalizeUserProperties(userData, true);
        await UserService.saveUser(jwt, normalizedData);
        displayAlert("Потребителят е създаден успешно!", "success");
      }
    } catch (error) {
      displayAlert("Грешка при създаването на потребител!", "error");
    }
  };

  const handleUserSelect = (value: string) => {
    setSelectedUserId(value);
    fetchUserData(value);
  };
  const handleOfficeSelect = (value: string) => {
    setSelectedOfficeId(value);
    setUserData((prev) => (prev ? { ...prev, divisions: value } : null));
  };
  const handleUserTypeSelect = (value: string) => {
    setUserData((prev) => (prev ? { ...prev, isUser: +value } : null));
  };
  const handleUserGroupSelect = (value: string) => {
    setUserData((prev) =>
      prev ? { ...prev, userGroup: value as UserAccessType } : null
    );
  };

  return (
    <>
      <Breadcrumb pageName="Потребители" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Управление на потребители" />

        <form onSubmit={saveUserData} className="p-3">
          {/* All 7 fields in one responsive grid */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Избор на потребител</label>
              <SelectGroup
                value={selectedUserId}
                placeholder="Нов потребител"
                options={users}
                onChange={handleUserSelect}
              />
            </div>
            <div>
              <label className={labelClass}>Имена на потребител</label>
              <input
                type="text"
                value={userData?.userName || ""}
                onChange={(e) =>
                  setUserData((prev) => ({ ...prev, userName: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Потребителско име</label>
              <input
                type="text"
                value={userData?.fullName || ""}
                onChange={(e) =>
                  setUserData((prev) => ({ ...prev, fullName: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Парола</label>
              <input
                type="password"
                value={userData?.password || ""}
                onChange={(e) =>
                  setUserData((prev) => ({ ...prev, password: e.target.value }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Тип потребител</label>
              <SelectGroup
                value={userData?.isUser?.toString() || ""}
                options={{ "1": "Потребител", "0": "Администратор" }}
                onChange={handleUserTypeSelect}
              />
            </div>
            <div>
              <label className={labelClass}>Група</label>
              <SelectGroup
                value={userData?.userGroup || ""}
                options={{
                  admin: "Администратор",
                  power: "Отговорник на офис",
                  user: "Офис потребител",
                  restricted_user: "Ограничен потребител",
                  noaccess: "Без достъп",
                }}
                onChange={handleUserGroupSelect}
              />
            </div>
            <div>
              <label className={labelClass}>Офиси</label>
              <SelectGroup
                value={
                  selectedOfficeId
                    ? selectedOfficeId
                    : userData?.divisions
                    ? userData.divisions
                    : ""
                }
                placeholder="Изберете офис"
                options={offices}
                onChange={handleOfficeSelect}
              />
            </div>
          </div>

          <div className="mt-3 flex justify-center">
            <button
              type="submit"
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Запис
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default UserManagement;
